import { useRef, useEffect, useCallback, useMemo } from "react";
import { Button, Select, Text } from "@radix-ui/themes";
import { useQuery } from "react-query";
import styles from "./enrichmentForm.module.scss";
import type { EnrichmentOption } from "@/v2/entities/enrichment/Enrichment.model";
import { triggerTypes } from "@/v2/entities/enrichment/Enrichment.model";
import { useTopicApi } from "@/v2/entities/topic/Topic.api";
import { MultiSelect } from "@/components/multi-select";
import type { Option } from "@/components/multi-select";
import ClassificationOptionsForm from "./ClassificationOptionsForm";

export type EnrichmentType = "classification" | "extraction";

export function formatTriggerLabel(trigger: string) {
  return trigger
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface EnrichmentReviewFormProps {
  name: string;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  task: string;
  onTaskChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enrichmentType: EnrichmentType;
  options: EnrichmentOption[];
  onOptionsChange: (options: EnrichmentOption[]) => void;

  trigger: string;
  onTriggerChange: (value: string) => void;
  topicIds?: number[];
  onTopicIdsChange?: (topicIds: number[]) => void;
  onSubmit: () => void;
  loading: boolean;
  submitLabel: string;
}

function EnrichmentReviewForm({
  name,
  onNameChange,
  task,
  onTaskChange,
  enrichmentType,
  options,
  onOptionsChange,

  trigger,
  onTriggerChange,
  topicIds,
  onTopicIdsChange,
  onSubmit,
  loading,
  submitLabel,
}: EnrichmentReviewFormProps) {
  const taskTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (taskTextareaRef.current) {
      taskTextareaRef.current.style.height = "auto";
      taskTextareaRef.current.style.height = `${Math.min(taskTextareaRef.current.scrollHeight, 240)}px`;
    }
  }, [task]);

  const { getTopics } = useTopicApi();
  const { data: topics } = useQuery({
    queryKey: ["topics"],
    queryFn: () => getTopics(),
  });

  const handleTriggerChange = useCallback((value: string) => {
    onTriggerChange(value);
    if (value === "ON_CREATION" && onTopicIdsChange) {
      onTopicIdsChange([]);
    }
  }, [onTriggerChange, onTopicIdsChange]);

  const topicOptions = useMemo<Option[]>(() => {
    if (!topics) return [];
    return topics.map((topic) => ({
      value: String(topic.id),
      label: topic.topic,
    }));
  }, [topics]);

  const selectedTopicStrings = useMemo(() => {
    return (topicIds ?? []).map(String);
  }, [topicIds]);

  const handleTopicSelectionChange = useCallback(
    (selected: string[]) => {
      if (!onTopicIdsChange) return;
      onTopicIdsChange(selected.map(Number));
    },
    [onTopicIdsChange]
  );

  return (
    <div className={styles.centeredColumn}>
      <div className={styles.nameSection}>
        <Text size="2" weight="medium" color="gray">
          Name
        </Text>
        <input
          value={name}
          onChange={onNameChange}
          placeholder="Enrichment name"
          className={styles.nameInput}
        />
      </div>

      <div className={styles.taskSection}>
        <Text size="2" weight="medium" color="gray">
          Task
        </Text>
        <textarea
          ref={taskTextareaRef}
          value={task}
          onChange={onTaskChange}
          placeholder="The task description for the AI..."
          className={styles.taskTextarea}
        />
      </div>

      {enrichmentType === "classification" && (
        <ClassificationOptionsForm
          options={options}
          onOptionsChange={onOptionsChange}
        />
      )}

      <div className={styles.triggerSection}>
        <Text size="2" weight="medium" color="gray">
          Trigger
        </Text>
        <Select.Root value={trigger} onValueChange={handleTriggerChange}>
          <Select.Trigger className={styles.triggerSelect} />
          <Select.Content position="popper" sideOffset={5}>
            <Select.Group>
              {triggerTypes.map((t) => (
                <Select.Item key={t} value={t}>
                  {formatTriggerLabel(t)}
                </Select.Item>
              ))}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </div>

      {onTopicIdsChange && topicOptions.length > 0 && trigger !== "ON_CREATION" && (
        <div className={styles.triggerSection}>
          <Text size="2" weight="medium" color="gray">
            Apply to Topics
          </Text>
          <Text size="1" color="gray">
            Leave empty to apply to all topics.
          </Text>
          <MultiSelect
            options={topicOptions}
            selected={selectedTopicStrings}
            onChange={handleTopicSelectionChange}
            placeholder="Select topics..."
          />
        </div>
      )}

      <div className={styles.approveButtonWrapper}>
        <Button
          onClick={onSubmit}
          size="3"
          color="gray"
          highContrast
          loading={loading}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

export default EnrichmentReviewForm;
